/* Live Custom Belts — 3D belt renderer. Zees Belts original. 2026-09-13 (v3).

   Rebuilt for photographic quality:
   • the leather is one seamless extrusion with belt-space UVs, a real grain/pattern
     normal, burnished edges and individually modelled saddle stitches, eyelets and snaps;
   • plates are stacked tiers with true bevels, sitting exactly on the belt axis at every
     layer count;
   • ornaments are embossed (alpha + bump + contact shadow), never flat decals;
   • dual plating puts one metal on the plate and the other on every raised object —
     no gradients anywhere;
   • a 2048px procedural studio environment does the heavy lifting on the gold;
   • front and back views for the 360 spin and the mockup PDF.  */
(function (root) {
  'use strict';
  var T = null;                      // THREE, injected on init
  var U = 100;                       // belt units per world unit
  var BEND = 0.0105;                 // gentle left-right wrap (product-shot, not a barrel)
  var DOME = 0.020;                  // gentle top-to-bottom doming
  var CYU = 260;                     // belt axis in belt units

  function lin(hex) { var c = new T.Color(hex); return c.convertSRGBToLinear ? c.convertSRGBToLinear() : c; }
  function v3x(x) { return (x - 800) / U; }
  function v3y(y) { return -(y - CYU) / U; }

  /* ---------- shape conversion ---------- */
  function feed(s, path) {
    var started = false;
    path.c.forEach(function (c) {
      if (c[0] === 0) { s.moveTo(v3x(c[1]), v3y(c[2])); started = true; }
      else if (c[0] === 1) s.lineTo(v3x(c[1]), v3y(c[2]));
      else if (c[0] === 2) s.quadraticCurveTo(v3x(c[1]), v3y(c[2]), v3x(c[3]), v3y(c[4]));
      else if (c[0] === 3) s.bezierCurveTo(v3x(c[1]), v3y(c[2]), v3x(c[3]), v3y(c[4]), v3x(c[5]), v3y(c[6]));
      else if (c[0] === 4 && started) s.closePath();
    });
    return s;
  }
  function toShape(path, hole) {
    var s = feed(new T.Shape(), path);
    if (hole) s.holes.push(feed(new T.Path(), hole));
    return s;
  }
  function beltUV(x0, y0, w, h) {
    return {
      generateTopUV: function (g, v, a, b, c) {
        return [a, b, c].map(function (i) { return new T.Vector2((v[i * 3] - x0) / w, (v[i * 3 + 1] - y0) / h); });
      },
      generateSideWallUV: function (g, v, a, b, c, d) {
        return [a, b, c, d].map(function (i) { return new T.Vector2((v[i * 3] - x0) / w, (v[i * 3 + 1] - y0) / h); });
      }
    };
  }
  var BEV = { soft: 0.030, deep: 0.034, flat: 0, def: 0.014 };
  function bevT(bevel) { return bevel === false ? BEV.flat : (bevel === 'soft' ? BEV.soft : (bevel === 'deep' ? BEV.deep : BEV.def)); }
  function extrude(path, depth, base, bevel, uv, hole) {
    var bt = bevT(bevel);
    var o = {
      depth: depth, bevelEnabled: bevel !== false,
      bevelThickness: bt,
      bevelSize: bevel === false ? 0 : (bevel === 'soft' ? 0.030 : (bevel === 'deep' ? 0.040 : 0.016)),
      bevelOffset: 0, bevelSegments: bevel === false ? 0 : (bevel === 'deep' ? 5 : 4), curveSegments: 24
    };
    if (uv) o.UVGenerator = uv;
    var g = new T.ExtrudeGeometry(toShape(path, hole), o);
    g.translate(0, 0, base);
    bend(g);
    return g;
  }
  /* the z the front face of such an extrusion actually ends up at */
  function faceZ(base, depth, bevel) { return base + depth + bevT(bevel); }
  function bend(g) {
    var p = g.attributes.position, a = p.array;
    for (var i = 0; i < a.length; i += 3) a[i + 2] -= a[i] * a[i] * BEND + a[i + 1] * a[i + 1] * DOME;
    p.needsUpdate = true;
    g.computeVertexNormals();
    return g;
  }
  function zAt(x, y) { return -(x * x * BEND + y * y * DOME); }
  /* a flat, well-segmented panel in belt space, laid on the bent surface */
  function panel(x, y, w, h, z) {
    var g = new T.PlaneGeometry(w / U, h / U, Math.max(10, Math.round(w / 18)), Math.max(4, Math.round(h / 18)));
    g.translate(v3x(x + w / 2), v3y(y + h / 2), z);
    bend(g);
    return g;
  }

  /* =========================================================================
     PROCEDURAL STUDIO ENVIRONMENT
     A 2048px equirect: bright cyc above, a hard horizon line, a lit table below
     and two big softboxes. This is what turns the metal from "yellow plastic"
     into gold, so it is worth the pixels.
     ========================================================================= */
  function envTexture() {
    var Wd = 2048, Ht = 1024;
    var c = document.createElement('canvas'); c.width = Wd; c.height = Ht;
    var x = c.getContext('2d');
    var g = x.createLinearGradient(0, 0, 0, Ht);
    /* muted ramp. A very bright cyc reflected in polished gold reads as a
       white blow-out that swims about as the belt turns, so the whole
       environment is kept well below clipping and the contrast is carried by
       the soft horizon rather than by hard white. */
    g.addColorStop(0.00, '#d8dce2');
    g.addColorStop(0.16, '#cdd2d9');
    g.addColorStop(0.30, '#a8aeb7');
    g.addColorStop(0.42, '#6a707a');
    g.addColorStop(0.470, '#31353c');
    g.addColorStop(0.505, '#181b21');   // the soft horizon band: gives metal its defining edge
    g.addColorStop(0.545, '#3b3f47');
    g.addColorStop(0.62, '#9ba1a9');
    g.addColorStop(0.76, '#c2c7ce');
    g.addColorStop(0.90, '#868b93');
    g.addColorStop(1.00, '#33363c');
    x.fillStyle = g; x.fillRect(0, 0, Wd, Ht);

    function soft(cx, cy, rx, ry, a, colr) {
      var rg = x.createRadialGradient(0, 0, 0, 0, 0, 1);
      rg.addColorStop(0, 'rgba(' + colr + ',' + a + ')');
      rg.addColorStop(0.45, 'rgba(' + colr + ',' + (a * 0.62) + ')');
      rg.addColorStop(0.78, 'rgba(' + colr + ',' + (a * 0.18) + ')');
      rg.addColorStop(1, 'rgba(' + colr + ',0)');
      x.save(); x.translate(cx, cy); x.scale(rx, ry);
      x.fillStyle = rg; x.beginPath(); x.arc(0, 0, 1, 0, 7); x.fill(); x.restore();
    }
    /* the big frontal softbox — a flat plate facing the camera reflects this.
       Large and gentle: a wide, low-intensity source wraps the bevels instead
       of stamping a hot dot on them. */
    soft(1024, 300, 980, 300, 0.62, '250,246,238');
    soft(1024, 110, 1020, 190, 0.50, '244,246,250');
    soft(290, 258, 420, 300, 0.46, '248,234,210');   // warm key, camera-left
    soft(1768, 276, 400, 290, 0.40, '214,228,246');  // cool fill, camera-right
    soft(1024, 782, 1000, 240, 0.34, '226,229,234');  // table bounce
    /* strip lights give the bevels a readable streak. They are drawn as soft
       vertical gradients, never hard bars — a hard-edged strip reflected in a
       curved plate is exactly the flickering glare the belt must not have. */
    function strip(sy, h, a, colr) {
      var lg = x.createLinearGradient(0, sy, 0, sy + h);
      lg.addColorStop(0, 'rgba(' + colr + ',0)');
      lg.addColorStop(0.5, 'rgba(' + colr + ',' + a + ')');
      lg.addColorStop(1, 'rgba(' + colr + ',0)');
      x.fillStyle = lg; x.fillRect(0, sy, Wd, h);
    }
    strip(14, 108, 0.42, '255,255,255');
    strip(228, 56, 0.20, '255,252,244');
    strip(686, 52, 0.12, '255,255,255');
    /* one gentle blur pass removes any remaining hard seam so nothing in the
       reflection has an edge sharp enough to sparkle as the belt rotates */
    try {
      var bc = document.createElement('canvas'); bc.width = Wd; bc.height = Ht;
      var bx = bc.getContext('2d');
      bx.filter = 'blur(9px)';
      bx.drawImage(c, 0, 0);
      x.filter = 'none';
      x.clearRect(0, 0, Wd, Ht);
      x.drawImage(bc, 0, 0);
    } catch (e) { }
    var t = new T.CanvasTexture(c);
    t.mapping = T.EquirectangularReflectionMapping;
    return t;
  }

  /* =========================================================================
     LEATHER GRAIN + PATTERN
     Greyscale, biased bright so `color x map` keeps the chosen hide colour.
     Used simultaneously as map, bumpMap and roughnessMap.
     ========================================================================= */
  var patCache = {};
  function hideCanvas(pattern, pal) {
    var ck = pattern + '|' + (pal ? pal.join(',') : '');
    if (patCache[ck]) return patCache[ck];
    var S = 1024, c = document.createElement('canvas'); c.width = c.height = S;
    var x = c.getContext('2d'), i, j;
    /* with a palette the canvas becomes a real colour map — camouflage has to be
       multi-tone, and a tinted greyscale just reads as one flat colour */
    var P4 = pal && pal.length >= 4 ? pal : null;
    function tone(k) { return P4 ? P4[k % 4] : ['#a9a9a9', '#f2f2f2', '#8c8c8c', '#c4c4c4'][k % 4]; }
    function noise(alpha, size, lo, hi) {
      var n = (S * S) / (size * size) * 0.55;
      for (var k = 0; k < n; k++) {
        var v = lo + Math.random() * (hi - lo) | 0;
        x.fillStyle = 'rgba(' + v + ',' + v + ',' + v + ',' + alpha + ')';
        x.fillRect(Math.random() * S, Math.random() * S, size, size);
      }
    }
    function cell(px, py, w, h, r, tone) {
      x.beginPath();
      if (x.roundRect) x.roundRect(px, py, w, h, r); else x.rect(px, py, w, h);
      var gr = x.createLinearGradient(px, py, px + w * 0.2, py + h);
      gr.addColorStop(0, tone[0]); gr.addColorStop(0.55, tone[1]); gr.addColorStop(1, tone[2]);
      x.fillStyle = gr; x.fill();
      x.strokeStyle = 'rgba(40,40,40,.85)'; x.lineWidth = Math.max(1.5, h * 0.045); x.stroke();
    }
    var LIGHT = ['#ffffff', '#e2e2e2', '#9d9d9d'], MID = ['#f0f0f0', '#cfcfcf', '#8a8a8a'];

    x.fillStyle = '#e8e8e8'; x.fillRect(0, 0, S, S);

    if (pattern === 'alligator') {
      for (j = -1; j < 9; j++) for (i = -1; i < 8; i++) {
        var aw = 128 + (j % 2 ? 14 : 0);
        cell(i * 148 + (j % 2 ? 40 : 0), j * 126, aw, 114, 26, (i + j) % 3 === 0 ? LIGHT : MID);
      }
    } else if (pattern === 'python') {
      for (j = -1; j < 15; j++) for (i = -1; i < 17; i++) cell(i * 64 + (j % 2 ? 32 : 0), j * 72, 56, 62, 24, (i + j) % 4 < 2 ? LIGHT : MID);
    } else if (pattern === 'crocotail') {
      for (j = -1; j < 10; j++) { var n2 = 5 + (j % 2); for (i = 0; i < n2; i++) cell(i * (S / n2) + 8, j * 114, S / n2 - 18, 100, 16, j % 2 ? LIGHT : MID); }
    } else if (pattern === 'lizard') {
      for (j = -1; j < 23; j++) for (i = -1; i < 23; i++) cell(i * 46 + (j % 2 ? 23 : 0), j * 46, 38, 38, 16, (i * j) % 5 === 0 ? LIGHT : MID);
    } else if (pattern === 'ostrich') {
      for (i = 0; i < 1500; i++) {
        var ox = Math.random() * S, oy = Math.random() * S, orr = 11 + Math.random() * 13;
        var rg = x.createRadialGradient(ox - orr * 0.35, oy - orr * 0.35, 1, ox, oy, orr);
        rg.addColorStop(0, '#ffffff'); rg.addColorStop(0.6, '#d2d2d2'); rg.addColorStop(1, '#8c8c8c');
        x.fillStyle = rg; x.beginPath(); x.arc(ox, oy, orr, 0, 7); x.fill();
      }
    } else if (pattern === 'carbon') {
      for (j = 0; j < 16; j++) for (i = 0; i < 16; i++) {
        var d = (i + j) % 2;
        x.fillStyle = d ? '#efefef' : '#a8a8a8';
        x.fillRect(i * 64, j * 64, 64, 64);
        x.fillStyle = 'rgba(0,0,0,.18)';
        for (var k2 = 0; k2 < 16; k2++) x.fillRect(i * 64 + (d ? k2 * 4 : 0), j * 64 + (d ? 0 : k2 * 4), d ? 2 : 64, d ? 64 : 2);
      }
    } else if (pattern === 'quilted') {
      x.fillStyle = '#dcdcdc'; x.fillRect(0, 0, S, S);
      for (j = -1; j < 8; j++) for (i = -1; i < 8; i++) {
        var qx = i * 146 + (j % 2 ? 73 : 0), qy = j * 146;
        var qg = x.createRadialGradient(qx + 60, qy + 50, 4, qx + 73, qy + 73, 100);
        qg.addColorStop(0, '#ffffff'); qg.addColorStop(1, '#a6a6a6');
        x.fillStyle = qg; x.beginPath();
        if (x.roundRect) x.roundRect(qx + 6, qy + 6, 134, 134, 34); else x.rect(qx + 6, qy + 6, 134, 134);
        x.fill();
        x.strokeStyle = 'rgba(50,50,50,.75)'; x.lineWidth = 4; x.stroke();
      }
    } else if (pattern === 'camo') {
      /* multi-tone woodland blotches — a pattern, not an emboss */
      x.fillStyle = P4 ? P4[3] : '#dedede'; x.fillRect(0, 0, S, S);
      [[tone(0), 30], [tone(1), 26], [tone(2), 22], [tone(3), 18]].forEach(function (spec) {
        x.fillStyle = spec[0];
        for (i = 0; i < spec[1]; i++) {
          var bx = Math.random() * S, by = Math.random() * S, pts = 8 + (Math.random() * 4 | 0);
          x.beginPath();
          for (var q = 0; q <= pts; q++) {
            var ang = q * 2 * Math.PI / pts, rad = 46 + Math.random() * 78;
            var px = bx + Math.cos(ang) * rad, py = by + Math.sin(ang) * rad * 0.72;
            if (q === 0) x.moveTo(px, py); else x.lineTo(px, py);
          }
          x.closePath(); x.fill();
        }
      });
    } else if (pattern === 'digicamo') {
      x.fillStyle = P4 ? P4[3] : '#d9d9d9'; x.fillRect(0, 0, S, S);
      var pal2b = [tone(0), tone(1), tone(2), tone(3)], px2 = 32;
      for (j = 0; j < S / px2; j++) for (i = 0; i < S / px2; i++) {
        var seed = Math.sin(i * 12.9898 + j * 78.233) * 43758.5453;
        var v2 = Math.abs(seed - Math.floor(seed));
        var cl = pal2b[Math.floor(v2 * 4) % 4];
        if (v2 > 0.42) { x.fillStyle = cl; x.fillRect(i * px2, j * px2, px2, px2); }
      }
    } else if (pattern === 'hexcamo') {
      x.fillStyle = P4 ? P4[3] : '#e0e0e0'; x.fillRect(0, 0, S, S);
      var pal2 = [tone(0), tone(1), tone(2), tone(3)], hr = 44;
      for (j = 0; j < 16; j++) for (i = 0; i < 16; i++) {
        var hx = i * hr * 1.5, hy = j * hr * 1.732 + (i % 2 ? hr * 0.866 : 0);
        x.fillStyle = pal2[(i * 3 + j * 5) % 4];
        x.beginPath();
        for (var hq = 0; hq < 6; hq++) { var ha = hq * Math.PI / 3; (hq ? x.lineTo : x.moveTo).call(x, hx + hr * Math.cos(ha), hy + hr * Math.sin(ha)); }
        x.closePath(); x.fill();
      }
    } else if (pattern === 'hairon') {
      x.fillStyle = P4 ? P4[3] : '#d8d8d8'; x.fillRect(0, 0, S, S);
      x.globalAlpha = 0.55;
      for (i = 0; i < 3200; i++) {
        x.fillStyle = P4 ? (i % 2 ? P4[0] : P4[2]) : 'rgb(' + (30 + Math.random() * 40 | 0) + ',' + (28 + Math.random() * 38 | 0) + ',' + (26 + Math.random() * 34 | 0) + ')';
        var pw = 70 + Math.random() * 150, ph = 50 + Math.random() * 120;
        x.beginPath(); x.ellipse(Math.random() * S, Math.random() * S, pw, ph, Math.random() * 3, 0, 7); x.fill();
      }
      x.globalAlpha = 1;
      for (i = 0; i < 9000; i++) {
        x.strokeStyle = 'rgba(255,255,255,.16)'; x.lineWidth = 1.6; x.beginPath();
        var hx2 = Math.random() * S, hy2 = Math.random() * S;
        x.moveTo(hx2, hy2); x.lineTo(hx2 + 8 + Math.random() * 14, hy2 + Math.random() * 5 - 2.5); x.stroke();
      }
    } else if (pattern === 'marble') {
      x.fillStyle = P4 ? P4[0] : '#ececec'; x.fillRect(0, 0, S, S);
      x.globalAlpha = P4 ? 0.34 : 0.24;
      for (i = 0; i < 90; i++) {
        x.strokeStyle = P4 ? (i % 2 ? P4[3] : P4[2]) : (i % 2 ? '#ffffff' : '#6e6e6e');
        x.lineWidth = 2 + Math.random() * 14; x.beginPath();
        var mx = Math.random() * S, my = Math.random() * S; x.moveTo(mx, my);
        for (var m = 0; m < 7; m++) { mx += (Math.random() - 0.5) * 340; my += (Math.random() - 0.5) * 340; x.lineTo(mx, my); }
        x.stroke();
      }
      x.globalAlpha = 1;
    } else if (pattern === 'snakeskin') {
      for (j = -1; j < 20; j++) for (i = -1; i < 26; i++) {
        var sx = i * 42 + (j % 2 ? 21 : 0), sy = j * 54;
        x.beginPath(); x.moveTo(sx + 21, sy); x.quadraticCurveTo(sx + 42, sy + 26, sx + 21, sy + 52);
        x.quadraticCurveTo(sx, sy + 26, sx + 21, sy);
        var sg = x.createLinearGradient(sx, sy, sx, sy + 52);
        sg.addColorStop(0, P4 ? ((i + j) % 3 ? P4[1] : P4[3]) : ((i + j) % 3 ? '#fbfbfb' : '#cccccc'));
        sg.addColorStop(1, P4 ? P4[2] : '#8e8e8e');
        x.fillStyle = sg; x.fill();
        x.strokeStyle = 'rgba(45,45,45,.8)'; x.lineWidth = 2; x.stroke();
      }
    } else { /* plain full-grain leather */
      noise(0.55, 3, 190, 255);
      noise(0.40, 2, 120, 200);
      for (i = 0; i < 340; i++) {
        x.strokeStyle = 'rgba(120,120,120,' + (0.10 + Math.random() * 0.22) + ')';
        x.lineWidth = 0.8 + Math.random() * 2.2; x.beginPath();
        var lx = Math.random() * S, ly = Math.random() * S; x.moveTo(lx, ly);
        for (var w2 = 0; w2 < 5; w2++) { lx += (Math.random() - 0.5) * 130; ly += (Math.random() - 0.5) * 130; x.lineTo(lx, ly); }
        x.stroke();
      }
      for (i = 0; i < 900; i++) {
        var pr = 2 + Math.random() * 4;
        x.fillStyle = 'rgba(255,255,255,.18)';
        x.beginPath(); x.arc(Math.random() * S, Math.random() * S, pr, 0, 7); x.fill();
      }
    }
    if (!P4 && pattern !== 'carbon' && pattern !== 'digicamo' && pattern !== 'hexcamo') noise(0.16, 2, 140, 255);
    var t = new T.CanvasTexture(c);
    t.wrapS = t.wrapT = T.RepeatWrapping;
    t.anisotropy = 8;
    if (P4) t.encoding = T.sRGBEncoding;
    patCache[ck] = t;
    return t;
  }

  /* =========================================================================
     LEATHER NORMAL MAP
     The same fBm height field that was authored for the modelled belt body, run
     here so the hide keeps its grain without shipping a texture download.
     height -> tangent-space normal, tiling, cached once per session.
     ========================================================================= */
  var grainTex = null;
  /* Leather grain, ported 1:1 from the Blender/Higgsfield authoring scene:
     a Voronoi F2-F1 pebble crease network carries the structure, smooth
     multi-octave value noise carries the tonal drift, and the tangent normal
     plus a matching roughness map are derived from the same height field.
     Computed once, wrapping cleanly, so nothing has to be downloaded. */
  var grainRough = null;
  function buildGrain() {
    if (grainTex) return;
    /* full resolution on desktop; half on phones, where the belt is drawn
       small and the extra synthesis time would show up as input lag */
    var small = (typeof window !== 'undefined' && window.innerWidth && window.innerWidth < 820);
    var N = small ? 512 : 1024, NC = 96;       // tile px, pebble cells across
    var rnd = (function (seed) {
      return function () { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
    })(4417);

    /* ---- smooth (cubic) value-noise octaves: no nearest-neighbour blocking ---- */
    function lattice(f) {
      var g = new Float32Array((f + 1) * (f + 1)), i, j;
      for (j = 0; j < f; j++) for (i = 0; i < f; i++) g[j * (f + 1) + i] = rnd();
      for (j = 0; j < f; j++) g[j * (f + 1) + f] = g[j * (f + 1)];
      for (i = 0; i <= f; i++) g[f * (f + 1) + i] = g[i];
      return g;
    }
    function fbm(octaves, persistence, base) {
      var out = new Float32Array(N * N);
      var amp = 1, freq = base, total = 0, o, j, i;
      for (o = 0; o < octaves; o++) {
        var g = lattice(freq), st = freq + 1;
        for (j = 0; j < N; j++) {
          var yy = j * freq / N, y0 = yy | 0, fy = yy - y0;
          fy = fy * fy * (3 - 2 * fy);
          var r0 = y0 * st, r1 = (y0 + 1) * st, ro = j * N;
          for (i = 0; i < N; i++) {
            var xx = i * freq / N, x0 = xx | 0, fx = xx - x0;
            fx = fx * fx * (3 - 2 * fx);
            var a = g[r0 + x0], b = g[r0 + x0 + 1];
            var c = g[r1 + x0], d = g[r1 + x0 + 1];
            out[ro + i] += amp * ((a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy);
          }
        }
        total += amp; amp *= persistence; freq *= 2;
        if (freq > 512) break;
      }
      for (var k = 0; k < out.length; k++) out[k] /= total;
      return out;
    }

    /* ---- Voronoi F2-F1: the crease network between leather pebbles ---- */
    var jx = new Float32Array(NC * NC), jy = new Float32Array(NC * NC), q;
    for (q = 0; q < NC * NC; q++) { jx[q] = 0.11 + rnd() * 0.78; jy[q] = 0.11 + rnd() * 0.78; }
    var cell = N / NC;
    var pebble = new Float32Array(N * N);
    var ox, oy, gi, gj, px_, py_, dx_, dy_, dd;
    for (var jj = 0; jj < N; jj++) {
      var ys = jj / cell, cj = ys | 0, row = jj * N;
      for (var ii = 0; ii < N; ii++) {
        var xs = ii / cell, ci = xs | 0;
        var f1 = 1e9, f2 = 1e9;
        for (oy = -1; oy <= 1; oy++) {
          gj = cj + oy;
          var mj = ((gj % NC) + NC) % NC;
          for (ox = -1; ox <= 1; ox++) {
            gi = ci + ox;
            var mi = ((gi % NC) + NC) % NC;
            px_ = gi + jx[mj * NC + mi];
            py_ = gj + jy[mj * NC + mi];
            dx_ = xs - px_; dy_ = ys - py_;
            dd = Math.sqrt(dx_ * dx_ + dy_ * dy_);
            if (dd < f1) { f2 = f1; f1 = dd; } else if (dd < f2) { f2 = dd; }
          }
        }
        var cr = (f2 - f1) * 1.55;
        if (cr > 1) cr = 1;
        pebble[row + ii] = Math.pow(cr, 0.62);
      }
    }

    var mid = fbm(3, 0.55, 22), broad = fbm(2, 0.55, 6), fine = fbm(2, 0.5, 190);
    var h = new Float32Array(N * N), lo = 1e9, hi = -1e9, k2;
    for (k2 = 0; k2 < h.length; k2++) {
      var v = pebble[k2] * 0.60 + mid[k2] * 0.22 + broad[k2] * 0.12 + fine[k2] * 0.06;
      h[k2] = v;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    var span = (hi - lo) || 1;
    for (k2 = 0; k2 < h.length; k2++) {
      var t = (h[k2] - lo) / span;
      t = (t - 0.5) * 1.18 + 0.5;
      h[k2] = t < 0 ? 0 : (t > 1 ? 1 : t);
    }

    /* ---- tangent normal + matching roughness, wrapping gradients ---- */
    var cN = document.createElement('canvas'); cN.width = cN.height = N;
    var xN = cN.getContext('2d'), iN = xN.createImageData(N, N), pN = iN.data;
    var cR = document.createElement('canvas'); cR.width = cR.height = N;
    var xR = cR.getContext('2d'), iR = xR.createImageData(N, N), pR = iR.data;
    var STR = 3.4 * N / 512;    // keep the slope constant as the tile resolution changes
    for (var j3 = 0; j3 < N; j3++) {
      var ym = (j3 + N - 1) % N, yp = (j3 + 1) % N, r3 = j3 * N;
      for (var i3 = 0; i3 < N; i3++) {
        var xm = (i3 + N - 1) % N, xp = (i3 + 1) % N;
        var gx = (h[r3 + xp] - h[r3 + xm]) * STR;
        var gy = (h[yp * N + i3] - h[ym * N + i3]) * STR;
        var l = Math.sqrt(gx * gx + gy * gy + 1);
        var o3 = (r3 + i3) * 4;
        pN[o3] = ((-gx / l) * 0.5 + 0.5) * 255;
        pN[o3 + 1] = ((gy / l) * 0.5 + 0.5) * 255;
        pN[o3 + 2] = ((1 / l) * 0.5 + 0.5) * 255;
        pN[o3 + 3] = 255;
        var rv = 0.92 - h[r3 + i3] * 0.22;
        var rb = (rv < 0.5 ? 0.5 : (rv > 0.97 ? 0.97 : rv)) * 255;
        pR[o3] = pR[o3 + 1] = pR[o3 + 2] = rb;
        pR[o3 + 3] = 255;
      }
    }
    xN.putImageData(iN, 0, 0);
    xR.putImageData(iR, 0, 0);
    grainTex = new T.CanvasTexture(cN);
    grainTex.wrapS = grainTex.wrapT = T.RepeatWrapping;
    grainTex.anisotropy = 16;
    grainRough = new T.CanvasTexture(cR);
    grainRough.wrapS = grainRough.wrapT = T.RepeatWrapping;
    grainRough.anisotropy = 16;
  }
  function grainNormal() { buildGrain(); return grainTex; }
  function grainRoughness() { buildGrain(); return grainRough; }

  /* brushed-satin streaks, used as a roughness map on the metal */
  var brushTex = null;
  function brushedCanvas() {
    if (brushTex) return brushTex;
    var Wd = 1024, Ht = 256, c = document.createElement('canvas'); c.width = Wd; c.height = Ht;
    var x = c.getContext('2d');
    x.fillStyle = '#8a8a8a'; x.fillRect(0, 0, Wd, Ht);
    for (var i = 0; i < 16000; i++) {
      var v = 90 + Math.random() * 110 | 0;
      x.fillStyle = 'rgba(' + v + ',' + v + ',' + v + ',.5)';
      x.fillRect(Math.random() * Wd, Math.random() * Ht, 6 + Math.random() * 60, 1);
    }
    brushTex = new T.CanvasTexture(c);
    brushTex.wrapS = brushTex.wrapT = T.RepeatWrapping;
    brushTex.repeat.set(3, 1);
    brushTex.anisotropy = 8;
    return brushTex;
  }

  /* ---------- text drawn along a slot's baseline ---------- */
  function slotCanvas(slot, style, text, font, opts) {
    var pad = 24, G = root.ZBGeom;
    var bow = slot.arc ? Math.abs(slot.bow) : 0;
    var bx = slot.x0 - pad - 30, by = slot.y - pad - bow - 10;
    var bw = (slot.x1 - slot.x0) + pad * 2 + 60, bh = slot.h + pad * 2 + bow * 2 + 20;
    var K = 4.2;
    var c = document.createElement('canvas'); c.width = Math.round(bw * K); c.height = Math.round(bh * K);
    var x = c.getContext('2d');
    x.scale(K, K); x.translate(-bx, -by);
    x.fillStyle = '#fff';
    var size = Math.min(slot.h * 0.70, 34);
    x.textAlign = 'center'; x.textBaseline = 'alphabetic';
    var full = String(text || '');
    if (!full) return null;
    var tp = G.textPath(slot, style);
    var pts = samplePath(tp, 200);
    var total = pathLength(pts);
    /* Fit the word against the width the glyphs ACTUALLY occupy on the path:
       per-glyph advances plus the tracking between them, measured against the
       path the letters are set on. Measuring the untracked string against the
       slot box instead let a long word lay out wider than its own path, and
       pointAt() then clamped every overhanging glyph on top of the last one —
       which is why BELTS came out as BEL-S and HEAVYWEIGHT ran into itself. */
    var track0 = (opts && opts.track ? opts.track : 1.2);
    function layout(sz) {
      x.font = '700 ' + sz + 'px ' + font;
      var tr = track0 * (sz / 30), w = 0, k;
      for (k = 0; k < full.length; k++) w += x.measureText(full[k]).width + tr;
      return { w: w, tr: tr };
    }
    var avail = Math.max(8, total - Math.max(4, total * 0.05));
    var m = layout(size), pass = 0;
    while (m.w > avail && size > 6 && pass < 5) { size = Math.max(6, size * avail / m.w); m = layout(size); pass++; }
    var track = m.tr, textW = m.w, i;
    var start = (total - textW) / 2, d = start;
    for (i = 0; i < full.length; i++) {
      var ch = full[i], cw = x.measureText(ch).width + track;
      var pt = pointAt(pts, d + cw / 2);
      x.save(); x.translate(pt.x, pt.y); x.rotate(pt.a); x.fillText(ch, 0, 0); x.restore();
      d += cw;
    }
    return { canvas: c, x: bx, y: by, w: bw, h: bh };
  }
  function samplePath(path, n) {
    var pts = [], cur = null;
    path.c.forEach(function (s) {
      if (s[0] === 0) { cur = { x: s[1], y: s[2] }; pts.push(cur); }
      else if (s[0] === 1) { for (var i = 1; i <= n; i++) pts.push({ x: cur.x + (s[1] - cur.x) * i / n, y: cur.y + (s[2] - cur.y) * i / n }); cur = { x: s[1], y: s[2] }; }
      else if (s[0] === 2) {
        for (var j = 1; j <= n; j++) {
          var t = j / n, mt = 1 - t;
          pts.push({ x: mt * mt * cur.x + 2 * mt * t * s[1] + t * t * s[3], y: mt * mt * cur.y + 2 * mt * t * s[2] + t * t * s[4] });
        }
        cur = { x: s[3], y: s[4] };
      }
    });
    return pts;
  }
  function pathLength(p) { var L = 0; for (var i = 1; i < p.length; i++) L += Math.hypot(p[i].x - p[i - 1].x, p[i].y - p[i - 1].y); return L; }
  function pointAt(p, d) {
    var acc = 0;
    for (var i = 1; i < p.length; i++) {
      var seg = Math.hypot(p[i].x - p[i - 1].x, p[i].y - p[i - 1].y);
      if (acc + seg >= d) {
        var t = seg ? (d - acc) / seg : 0;
        return { x: p[i - 1].x + (p[i].x - p[i - 1].x) * t, y: p[i - 1].y + (p[i].y - p[i - 1].y) * t, a: Math.atan2(p[i].y - p[i - 1].y, p[i].x - p[i - 1].x) };
      }
      acc += seg;
    }
    var l = p[p.length - 1] || { x: 0, y: 0 };
    return { x: l.x, y: l.y, a: 0 };
  }
  function canvasTex(c, srgb) {
    var t = new T.CanvasTexture(c);
    t.anisotropy = 16;
    t.minFilter = T.LinearMipmapLinearFilter;
    if (srgb) t.encoding = T.sRGBEncoding;
    return t;
  }

  /* =========================================================================
     THE CONTROLLER
     ========================================================================= */
  function Belt(host) {
    this.host = host; this.ok = false;
    try {
      this.renderer = new T.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true, powerPreference: 'high-performance' });
    } catch (e) { return; }
    var r = this.renderer;
    this.dpr = Math.min((root.devicePixelRatio || 1) * 1.35, 2.6);
    r.setPixelRatio(this.dpr);
    r.outputEncoding = T.sRGBEncoding;
    r.toneMapping = T.ACESFilmicToneMapping;
    /* small screens get a lower pixel budget and therefore a coarser reflection
       probe, which turns any bright highlight into a crawling sparkle. Pull the
       exposure down there so nothing reaches the clipping shoulder. */
    this.small = root.innerWidth < 820;
    r.toneMappingExposure = this.small ? 0.90 : 0.99;
    r.shadowMap.enabled = false;
    host.appendChild(r.domElement);
    /* A bare <canvas> is announced as nothing at all, so a screen-reader user
       got silence where the whole preview is. role=img + a name makes it one
       announced object; the running commentary of what actually changed is
       already handled by the .zb-status live region, so this name stays
       static and does not chatter on every re-render. */
    r.domElement.setAttribute('role', 'img');
    r.domElement.setAttribute('aria-label',
      'Rotating 3D preview of the championship belt you are designing. ' +
      'The written summary below lists every choice you have made.');
    r.domElement.style.cssText = 'width:100%;height:100%;display:block;touch-action:pan-y';

    this.scene = new T.Scene();
    this.camera = new T.PerspectiveCamera(21, 3.0, 0.1, 100);
    this.camera.position.set(0, 0, 21.5);
    this.group = new T.Group(); this.scene.add(this.group);
    this.front = new T.Group(); this.back = new T.Group();
    this.group.add(this.front); this.group.add(this.back);
    this.back.visible = false;

    var pmrem = new T.PMREMGenerator(r); pmrem.compileEquirectangularShader();
    var envT = envTexture();
    this.env = pmrem.fromEquirectangular(envT).texture;
    envT.dispose(); pmrem.dispose();
    this.scene.environment = this.env;

    /* Directional lights add a second, much sharper specular lobe on top of the
       environment: on a curved metal plate that lobe sweeps across the surface
       as the belt turns and reads as cheap glare. They are kept only as a faint
       shaping wash for the leather and the recessed enamel; the environment
       alone lights the metal. */
    var key = new T.DirectionalLight(0xfff4e6, 0.34); key.position.set(-4.5, 6.5, 10); this.scene.add(key);
    var fill = new T.DirectionalLight(0xd6e3ff, 0.16); fill.position.set(7, -2.5, 8); this.scene.add(fill);
    var rimL = new T.DirectionalLight(0xffd9a2, 0.14); rimL.position.set(2.5, 5, -9); this.scene.add(rimL);
    this.scene.add(new T.AmbientLight(0xffffff, 0.30));

    this.rot = { x: -0.03, y: 0.0 }; this.target = { x: -0.03, y: 0.0 };
    /* No idle sway. It rendered a frame forever — on a throttled phone every
       frame is a long task, so the belt quietly ate the main thread (and the
       battery) for a movement nobody asked for, and made the pinned preview
       look like it was shivering while the page scrolled. */
    this.auto = false;
    this.spin = 0;
    this._dirtyUntil = 0; this._offscreen = false;
    this.zoom = 1; this.baseZ = 21.5;
    this.ok = true;
    this._bind();
    this._loop();
  }

  Belt.prototype._bind = function () {
    var self = this, el = this.renderer.domElement, drag = null;
    el.addEventListener('pointerdown', function (e) {
      if (e.target.closest && e.target.closest('.zb-art-hit')) return;
      drag = { x: e.clientX, y: e.clientY, rx: self.target.x, ry: self.target.y,
               px: self.pan ? self.pan.x : 0, py: self.pan ? self.pan.y : 0 };
      self.auto = false; self.spin = 0;
      try { el.setPointerCapture(e.pointerId); } catch (err) { }
    });
    el.addEventListener('pointermove', function (e) {
      if (!drag) return;
      /* Zoomed in, the customer is inspecting one plate, not turning the belt.
         A drag then slides the belt across the frame so they can go plate to
         plate and watch their own changes; spinning it would throw away the
         very detail they zoomed in for, and there is no shift key on a phone.
         Shift+drag still rotates, for a desktop customer who wants to turn it. */
      if ((self.zoom || 1) > 1.01 && !e.shiftKey) {
        var vis = 2 * Math.tan(self.camera.fov * Math.PI / 360) * self.camera.position.z;
        var pxu = vis / (self.host.clientHeight || 300);
        self.pan = self.pan || { x: 0, y: 0 };
        self.pan.x = (drag.px || 0) - (e.clientX - drag.x) * pxu;
        self.pan.y = (drag.py || 0) + (e.clientY - drag.y) * pxu;
        self._clampPan();
        return;
      }
      self.target.y = clamp(drag.ry + (e.clientX - drag.x) * 0.008, -Math.PI, Math.PI);
      self.target.x = clamp(drag.rx + (e.clientY - drag.y) * 0.004, -0.32, 0.32);
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (k) { el.addEventListener(k, function () { drag = null; }); });

    /* wheel / trackpad zoom. Only claimed once the pointer is over the belt and
       the gesture is a deliberate zoom, so the page still scrolls normally. */
    el.addEventListener('wheel', function (e) {
      if (!e.ctrlKey && Math.abs(e.deltaY) < 4) return;
      e.preventDefault();
      self.auto = false; self.spin = 0;
      self.zoomBy(Math.pow(0.9986, e.deltaY * (e.deltaMode === 1 ? 16 : 1)));
    }, { passive: false });

    /* two-finger pinch */
    var pts = {}, pinch = null;
    function dist() {
      var k = Object.keys(pts); if (k.length < 2) return 0;
      var a = pts[k[0]], b = pts[k[1]];
      return Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y));
    }
    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'touch') return;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (Object.keys(pts).length === 2) {
        drag = null; self.auto = false; self.spin = 0;
        pinch = { d: dist(), z: self.zoom || 1 };
        el.style.touchAction = 'none';
      }
    });
    el.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'touch' || !pts[e.pointerId]) return;
      pts[e.pointerId].x = e.clientX; pts[e.pointerId].y = e.clientY;
      if (!pinch) return;
      var d = dist();
      if (d > 8 && pinch.d > 8) self.zoomTo(pinch.z * (d / pinch.d));
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (k) {
      el.addEventListener(k, function (e) {
        delete pts[e.pointerId];
        if (Object.keys(pts).length < 2 && pinch) {
          pinch = null;
          el.style.touchAction = (self.zoom || 1) > 1.01 ? 'none' : 'pan-y';
        }
      });
    });

    this._ro = new ResizeObserver(function () { self.resize(); }); this._ro.observe(this.host);
    /* Scrolled past, or the tab is in the background: stop drawing entirely. */
    if (root.IntersectionObserver) {
      this._io = new IntersectionObserver(function (es) {
        self._offscreen = !es[es.length - 1].isIntersecting;
        if (!self._offscreen) self.invalidate(400);
      }, { rootMargin: '120px' });
      this._io.observe(this.host);
    }
    this._vis = function () { if (!root.document.hidden) self.invalidate(400); };
    root.document.addEventListener('visibilitychange', this._vis);
  };
  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
  function norm(a) { a = a % (Math.PI * 2); if (a > Math.PI) a -= Math.PI * 2; if (a < -Math.PI) a += Math.PI * 2; return a; }

  Belt.prototype.resize = function (force) {
    var w = this.host.clientWidth || 900, h = this.host.clientHeight || 300;
    if (!w || !h) return;
    /* ignore sub-pixel churn. A pinned canvas whose host is re-laid-out by the
       page while scrolling would otherwise be torn down and rebuilt every
       frame, which reads as the whole belt vibrating. A drawing buffer that no
       longer matches the element is never ignored, though: an off-screen
       capture leaves one behind, and skipping it would strand the canvas at the
       wrong resolution. */
    var pr = this.renderer.getPixelRatio(), cel = this.renderer.domElement;
    var okBuf = Math.abs(cel.width - Math.floor(w * pr)) <= 1 && Math.abs(cel.height - Math.floor(h * pr)) <= 1;
    if (!force && okBuf && Math.abs(w - (this._w || 0)) < 2 && Math.abs(h - (this._h || 0)) < 2) return;
    this._w = w; this._h = h;
    this.renderer.setSize(w, h, false);
    this._frame(w / h);
    this.invalidate(300);
  };
  Belt.prototype._frame = function (asp) {
    this.camera.aspect = asp;
    /* keep the whole 1548-unit belt in view whatever the container shape */
    var need = 16.4 / 2;                       // half the belt width in world units, plus air
    var fovH = 2 * Math.atan(Math.tan(21 * Math.PI / 360) * asp);
    var z = need / Math.tan(fovH / 2) + 0.6;
    this.camera.fov = 21;
    this.baseZ = Math.max(14, Math.min(46, z));
    this.camera.position.z = this.baseZ / (this.zoom || 1);
    this.camera.updateProjectionMatrix();
  };
  Belt.prototype.ZMIN = 1; Belt.prototype.ZMAX = 3.2;
  Belt.prototype.zoomTo = function (z) {
    this.zoom = clamp(z || 1, this.ZMIN, this.ZMAX);
    var zoomed = this.zoom > 1.01;
    /* a belt still turning under a zoomed-in customer is unusable */
    if (zoomed) { this.spin = 0; this.auto = false; }
    if (this.zoom <= 1.0001) { this.pan = null; }
    this.camera.position.z = this.baseZ / this.zoom;
    this._clampPan();
    this.camera.updateProjectionMatrix();
    this.invalidate(300);
    /* zoomed in, one finger dragging means "slide the belt", so the canvas has
       to keep that gesture instead of handing it to the page scroller */
    try { this.renderer.domElement.style.touchAction = zoomed ? 'none' : 'pan-y'; } catch (err) { }
    if (this.onzoom) this.onzoom(this.zoom);
  };
  Belt.prototype.zoomBy = function (k) { this.zoomTo((this.zoom || 1) * (k || 1)); };
  /* panning keeps the belt reachable once it no longer fits the frame */
  Belt.prototype._clampPan = function () {
    if (!this.pan) { this.camera.position.x = 0; this.camera.position.y = 0; return; }
    var vis = 2 * Math.tan(this.camera.fov * Math.PI / 360) * this.camera.position.z;
    var visW = vis * (this.camera.aspect || 3);
    var mx = Math.max(0, (16.4 - visW) / 2), my = Math.max(0, (5.2 - vis) / 2);
    this.pan.x = clamp(this.pan.x, -mx, mx); this.pan.y = clamp(this.pan.y, -my, my);
    this.camera.position.x = this.pan.x; this.camera.position.y = this.pan.y;
    this.camera.lookAt(this.pan.x, this.pan.y, 0);
    this.invalidate(300);
  };
  /* Ask for a redraw. The window covers work that lands a few frames later —
     a texture upload, a font swap, the environment map finishing. */
  Belt.prototype.invalidate = function (ms) {
    this._dirtyUntil = Date.now() + (ms || 500);
  };
  /* Freeze what is on screen without stopping the renderer. The thumbnail pass
     builds all twelve belt shapes through this same renderer and captures each
     one off-screen; without this, the customer's own belt visibly flickers
     through every other shape while that runs. capture() draws explicitly, so
     it still works while the visible frame is held. */
  Belt.prototype.suspend = function () { this._suspended = true; };
  Belt.prototype.resume = function () { this._suspended = false; this.invalidate(700); };
  Belt.prototype._loop = function () {
    var self = this;
    this._raf = requestAnimationFrame(function () { self._loop(); });
    /* Nothing to draw for a canvas nobody can see. */
    if (this._suspended || this._offscreen || (root.document && root.document.hidden)) return;
    if (this.spin) { this.target.y += this.spin; this.rot.y = this.target.y; }
    else if (this.auto) this.target.y = Math.sin(Date.now() / 4200) * 0.14;
    var dx = this.target.x - this.rot.x, dy = this.target.y - this.rot.y;
    var moving = !!this.spin || !!this.auto || Math.abs(dx) > 2e-4 || Math.abs(dy) > 2e-4;
    if (!this.spin) {
      this.rot.x += dx * 0.10;
      this.rot.y += dy * 0.10;
    }
    /* Idle: the picture is already on screen and correct — drawing it again
       every 16ms is pure heat. */
    if (!moving && Date.now() > this._dirtyUntil) return;
    if (Math.abs(this.rot.y) > Math.PI * 2) { this.rot.y = norm(this.rot.y); this.target.y = this.rot.y; }
    var flip = Math.abs(norm(this.rot.y)) > Math.PI / 2;
    this.front.visible = !flip; this.back.visible = flip;
    this.group.rotation.x = this.rot.x; this.group.rotation.y = this.rot.y;
    this.renderer.render(this.scene, this.camera);
  };
  Belt.prototype.setView = function (name) {
    this.auto = false; this.spin = 0;
    if (name === 'front') { this.target.x = 0; this.target.y = 0; }
    else if (name === 'angle') { this.target.x = -0.08; this.target.y = 0.40; }
    else if (name === 'tilt') { this.target.x = 0.20; this.target.y = -0.28; }
    else if (name === 'back') { this.target.x = 0; this.target.y = Math.PI; }
    else if (name === 'spin360') { this.spin = 0.018; this.zoomTo(1); }
    else if (name === 'spin') { this.auto = true; }
    this.invalidate(400);
  };
  Belt.prototype.dispose = function () {
    cancelAnimationFrame(this._raf);
    if (this._ro) this._ro.disconnect();
    if (this._io) this._io.disconnect();
    if (this._vis) root.document.removeEventListener('visibilitychange', this._vis);
    this.clear();
    this.renderer.dispose();
    if (this.renderer.domElement.parentNode) this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
  };
  Belt.prototype.clear = function () {
    [this.front, this.back].forEach(function (g) {
      while (g.children.length) {
        var m = g.children.pop();
        if (m.geometry) m.geometry.dispose();
        if (m.material) {
          ['alphaMap', 'map', 'bumpMap'].forEach(function (k) { if (m.material[k] && m.material[k].__own) m.material[k].dispose(); });
          m.material.dispose();
        }
      }
    });
  };

  /* ---------- materials ---------- */
  var SMALL = root.innerWidth < 820;
  function metalMat(hex, rough, satin) {
    /* No clearcoat on metal. A clearcoat layer over a metal base is not a thing
       that exists on a plated belt, and in the renderer it stacks a second
       mirror-sharp highlight on top of the metallic one — the single biggest
       source of the blown-out patches that slide around while the belt turns.
       envMapIntensity stays at 1.0, which is the physically correct value now
       that the environment itself is a real studio rather than a white box. */
    var r0 = rough === undefined ? 0.16 : rough;
    var m = new T.MeshPhysicalMaterial({
      color: lin(hex), metalness: 1.0,
      roughness: SMALL ? Math.min(0.62, r0 + 0.07) : r0,
      envMapIntensity: SMALL ? 0.86 : 1.0,
      clearcoat: 0, clearcoatRoughness: 0.2
    });
    if (satin) { m.roughnessMap = brushedCanvas(); }
    return m;
  }
  /* The nameplate's own enamel. The recessed field on a plate is deep gloss,
     but a small raised plaque catches the softbox across the exact patch of
     surface the lettering sits on, and the words disappear into the sheen.
     This one is a satin bake instead: it still reads as enamel, it just does
     not mirror the studio back at the customer. */
  function bannerMat(hex) {
    return new T.MeshPhysicalMaterial({
      color: lin(hex), metalness: 0.0, roughness: 0.55,
      clearcoat: 0.12, clearcoatRoughness: 0.5, envMapIntensity: 0.22
    });
  }
  function enamelMat(hex) {
    return new T.MeshPhysicalMaterial({
      color: lin(hex), metalness: 0.0, roughness: 0.30,
      clearcoat: 0.55, clearcoatRoughness: 0.16, envMapIntensity: 0.55
    });
  }
  var METAL = { gold: '#d6a92f', silver: '#c9ced4', nickel: '#c9ced4' };

  function metalsFor(st, data) {
    if (st.finish === 'dual') {
      var a = METAL[st.dualPrimary === 'silver' ? 'silver' : 'gold'];
      var b = METAL[st.dualSecondary === 'gold' ? 'gold' : 'silver'];
      return { body: a, trim: b, obj: b, dual: true };
    }
    var f = pick(data.finishes, st.finish) || data.finishes[0];
    return { body: f.body, trim: f.trim || f.body, obj: f.trim || f.body, dual: false };
  }

  /* =========================================================================
     BUILD
     ========================================================================= */
  Belt.prototype.build = function (geom, st, data, lite) {
    if (!this.ok) return;
    this.invalidate(1200);
    this.clear();
    var G = root.ZBGeom, g = this.front, gb = this.back;
    var mt = metalsFor(st, data);
    var satin = st.edge === 'brushed';
    /* mirror-polished plating still has a measurable microroughness; at 0.115 it
       behaves as a perfect mirror and every reflected highlight is a hard dot */
    var rough = satin ? 0.40 : 0.19;
    var bodyMat = metalMat(mt.body, rough, satin);
    /* the bead around a nameplate is brushed, never mirror: a polished ring
       right beside small type throws a highlight straight across the words */
    var bandEdgeMat = metalMat(mt.trim, 0.44, true);
    var trimMat = metalMat(mt.trim, satin ? 0.36 : 0.165, satin);
    var objMat = metalMat(mt.obj, satin ? 0.34 : 0.175, satin);

    /* ---------------- leather ---------------- */
    var strap = geom.strap;
    var sp = pick(data.straps, st.strap) || data.straps[0];
    var sbb = strap.band.bbox();
    var uv = beltUV(v3x(sbb.x), v3y(sbb.y + sbb.h), sbb.w / U, sbb.h / U);
    var pal = sp.colors && sp.colors.length >= 4 ? sp.colors : null;
    var tile = (sp.texture && sp.texture !== 'grain') ? 300 : 190;   // belt units per texture tile
    function fit(tex) {
      var t2 = tex.clone(); t2.needsUpdate = true;
      t2.wrapS = t2.wrapT = T.RepeatWrapping; t2.anisotropy = 16;
      t2.repeat.set(sbb.w / tile, sbb.h / tile);
      return t2;
    }
    var hideT = fit(hideCanvas(sp.texture || 'grain', pal));
    var bumpT = pal ? fit(hideCanvas(sp.texture || 'grain', null)) : hideT;
    var grainT = grainNormal().clone(); grainT.needsUpdate = true;
    grainT.wrapS = grainT.wrapT = T.RepeatWrapping; grainT.anisotropy = 16;
    grainT.repeat.set(sbb.w / 150, sbb.h / 150);
    var plainHide = !sp.texture || sp.texture === 'grain';
    /* the roughness that came out of the same height field as the grain:
       pebble crowns buff a touch smoother, the creases between them stay matte */
    var grainR = grainRoughness().clone(); grainR.needsUpdate = true;
    grainR.wrapS = grainR.wrapT = T.RepeatWrapping; grainR.anisotropy = 16;
    grainR.repeat.copy(grainT.repeat);
    var lm = new T.MeshPhysicalMaterial({
      color: pal ? 0xffffff : lin(sp.hex), metalness: 0, roughness: 0.90, envMapIntensity: 0.34,
      clearcoat: sp.texture === 'carbon' ? 0.7 : 0.13, clearcoatRoughness: 0.58
    });
    lm.map = hideT; lm.roughnessMap = plainHide ? grainR : bumpT;
    lm.normalMap = grainT;
    lm.normalScale = new T.Vector2(plainHide ? 0.95 : 0.42, plainHide ? 0.95 : 0.42);
    if (!plainHide) { lm.bumpMap = bumpT; lm.bumpScale = 0.050; }
    var LTH = 0.15;                                   // leather thickness
    g.add(new T.Mesh(extrude(strap.band, LTH, 0, 'soft', uv), lm));
    var S = faceZ(0, LTH, 'soft');                    // the leather's front surface

    /* saddle stitching */
    if (!lite) {
      var stMesh = stitchRun(strap.stitch, sp.stitch || '#e3d6a6', S + 0.006, 0);
      if (stMesh) g.add(stMesh);
    }

    /* eyelets (left tail), snap studs (right tail), metal end tip */
    var holeMat = new T.MeshStandardMaterial({ color: lin('#08080a'), roughness: 0.92, metalness: 0.1 });
    strap.eyelets.forEach(function (e) {
      g.add(new T.Mesh(discGeo(e.x, e.y, e.r * 0.62, S + 0.001), holeMat));
      var tor = new T.TorusGeometry((e.r * 0.80) / U, (e.r * 0.21) / U, 12, 34);
      tor.translate(v3x(e.x), v3y(e.y), S + 0.004);
      g.add(new T.Mesh(bend(tor), trimMat));
    });
    strap.snaps.forEach(function (n) { g.add(new T.Mesh(domeGeo(n.x, n.y, n.r, S + 0.001, 0.62), trimMat)); });
    g.add(new T.Mesh(extrude(strap.tip, 0.05, S - 0.028, true), trimMat));

    /* ---------------- the back of the belt ----------------
       Plain hide: only the backs of the snap fasteners, the eyelet rings and the
       metal tip are visible. Built at negative z so it reads correctly once the
       group has rotated past 90 degrees. */
    var lmb = new T.MeshPhysicalMaterial({
      color: pal ? 0xffffff : lin(sp.hex), metalness: 0, roughness: 0.96, envMapIntensity: 0.26, clearcoat: 0.05, clearcoatRoughness: 0.75
    });
    lmb.color.multiplyScalar(0.9);
    lmb.map = hideT; lmb.roughnessMap = plainHide ? grainR : bumpT;
    lmb.normalMap = grainT; lmb.normalScale = new T.Vector2(0.8, 0.8);
    if (!lite) gb.add(new T.Mesh(extrude(strap.band, LTH, 0, 'soft', uv), lmb));
    /* The leather's own back face sits at -bevelThickness (0.030), so every part
       of the reverse has to stand proud of that or the hide swallows it. */
    var BK = -0.030;                                   // the hide's back surface
    var backMetal = metalMat(mt.trim, satin ? 0.36 : 0.14, satin); backMetal.side = T.DoubleSide;
    var backDark = new T.MeshStandardMaterial({ color: lin('#0a0a0c'), roughness: 0.92, metalness: 0.05, side: T.DoubleSide });
    if (!lite) strap.snaps.forEach(function (n) {
      /* the domed cap on the back of a snap fastener, with its socket ring */
      gb.add(new T.Mesh(backDomeGeo(n.x, n.y, n.r * 0.88, BK - 0.088, 0.46), backMetal));
      gb.add(new T.Mesh(ringGeo(n.x, n.y, n.r * 0.30, n.r * 0.42, BK - 0.136), backDark));
    });
    if (!lite) strap.eyelets.forEach(function (e) {
      /* grommet ring standing proud of the hide, dark hole recessed behind it */
      gb.add(new T.Mesh(discGeo(e.x, e.y, e.r * 0.62, BK - 0.082), backDark));
      var tor = new T.TorusGeometry((e.r * 0.80) / U, (e.r * 0.20) / U, 10, 30);
      tor.translate(v3x(e.x), v3y(e.y), BK - 0.100);
      gb.add(new T.Mesh(bend(tor), backMetal));
    });
    /* the edge run of saddle stitching is the only detail that carries through */
    if (!lite) {
      var bsMesh = stitchRun(strap.stitch, sp.stitch || '#e3d6a6', BK - 0.082, 0);
      if (bsMesh) gb.add(bsMesh);
    }
    if (!lite) gb.add(new T.Mesh(extrude(strap.tip, 0.05, -0.088, true), backMetal));

    /* ---------------- plates ----------------
       Each plate is a stack of tiers. The top tier is cut as a FRAME with a hole,
       and a recessed enamel field sits behind that hole — the construction that
       gives a real championship plate its depth. */
    var enamelPick0 = pick(data.banners, st.bannerColor) || data.banners[0];
    var fCol = new T.Color(enamelPick0.hex || '#151210');
    var hsl = { h: 0, s: 0, l: 0 }; fCol.getHSL(hsl);
    fCol.setHSL(hsl.h, Math.min(hsl.s, 0.42), 0.055);   /* deep enamel, never muddy */
    var fieldMat = new T.MeshPhysicalMaterial({
      color: lin('#' + fCol.getHexString()), metalness: 0.18, roughness: 0.26,
      clearcoat: 1.0, clearcoatRoughness: 0.05, envMapIntensity: 0.62
    });
    var order = ['outerL', 'outerR', 'innerL', 'innerR', 'center'];
    order.forEach(function (key) {
      var pl = geom.plates[key];
      var isC = key === 'center';
      var d0 = isC ? 0.115 : key.indexOf('inner') === 0 ? 0.090 : 0.076;
      var dN = isC ? 0.055 : 0.036;
      var base = S + 0.002, zs = [];
      var last = pl.tiers.length - 1;
      pl.tiers.forEach(function (o, i) {
        var d = i === 0 ? d0 : dN;
        var bev = i === 0 ? 'deep' : true;
        var hole = (i === last && pl.field) ? pl.field.path : null;
        g.add(new T.Mesh(extrude(o, d, base, bev, null, hole), i % 2 === 1 ? trimMat : bodyMat));
        zs.push(faceZ(base, d, bev));
        base = base + d;
      });
      pl.tierZ = zs;
      pl.topZ = zs[last];
      if (pl.field) {
        var fBase = pl.topZ - (isC ? 0.052 : 0.040);
        g.add(new T.Mesh(extrude(pl.field.path, 0.012, fBase, true), fieldMat));
        pl.fieldZ = faceZ(fBase, 0.012, true);
      } else pl.fieldZ = pl.topZ;
    });

    /* ---------------- ornament, artwork, banners, lettering ---------------- */
    var font = (pick(data.fonts, st.font) || data.fonts[0]);
    order.forEach(function (key) {
      var pl = geom.plates[key];
      var fz = pl.fieldZ, fb = pl.field ? pl.field.box : pl.top.box, art = pl.art;

      var live = [];
      pl.slots.forEach(function (sl) {
        var cfg = st.zones[sl.id] || {};
        if (cfg.text) live.push({ slot: sl, cfg: cfg });
      });

      /* corner rivets on the flanking plates, as on a real mounted plate */
      if (!isCenter(key) && pl.top && pl.top.box) {
        var rb = pl.top.box, rr2 = Math.max(3.2, Math.min(rb.w, rb.h) * 0.055);
        [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(function (q) {
          g.add(new T.Mesh(domeGeo(rb.cx + q[0] * (rb.w / 2 - rr2 * 2.1), rb.cy + q[1] * (rb.h / 2 - rr2 * 2.1), rr2, pl.topZ, 0.62), trimMat));
        });
      }
      /* medallion bezel */
      if (art && art.r > 6) g.add(new T.Mesh(ringGeo(art.cx, art.cy, art.r * 1.00, art.r * 1.10, fz + 0.004), trimMat));

      /* ornament layout, embossed on the enamel field */
      var layId = isCenter(key) ? (st.layout || 'crest') : sideLayout(st.layout, key);
      var lay = G.layout(layId, art);
      if (lay && lay.length) {
        var pad = 10;
        var bx = fb.x - pad, by = fb.y - pad, bw = fb.w + pad * 2, bh = fb.h + pad * 2;
        var K = lite ? (isCenter(key) ? 1.6 : 2.2) : (isCenter(key) ? 3.2 : 4.4);
        var oc = document.createElement('canvas');
        oc.width = Math.round(bw * K); oc.height = Math.round(bh * K);
        var ox = oc.getContext('2d');
        ox.scale(K, K); ox.translate(-bx, -by);
        ox.fillStyle = '#fff'; ox.strokeStyle = '#fff'; ox.lineCap = 'round'; ox.lineJoin = 'round';
        lay.forEach(function (o) {
          var p2 = new Path2D(o.d);
          if (o.kind === 'stroke') { ox.lineWidth = o.w; ox.stroke(p2); } else ox.fill(p2);
        });
        /* never let the ornament sit under a banner: punch the live slots out */
        ox.globalCompositeOperation = 'destination-out';
        live.forEach(function (L) {
          var sl = L.slot, bo = sl.arc ? Math.abs(sl.bow) : 0;
          ox.fillStyle = '#000';
          ox.fillRect(sl.x0 - 14, sl.y - bo - 7, (sl.x1 - sl.x0) + 28, sl.h + bo + 14);
        });
        ox.globalCompositeOperation = 'source-over';

        var ob = document.createElement('canvas');
        ob.width = oc.width; ob.height = oc.height;
        var bxx = ob.getContext('2d');
        bxx.fillStyle = '#000'; bxx.fillRect(0, 0, ob.width, ob.height);
        try { bxx.filter = 'blur(' + Math.max(1.6, K * 1.0) + 'px)'; } catch (e) { }
        bxx.drawImage(oc, 0, 0);
        try { bxx.filter = 'none'; } catch (e) { }

        if (!lite) {
          var sh = new T.MeshStandardMaterial({ color: 0x000000, roughness: 1, metalness: 0, transparent: true, opacity: 0.6 });
          sh.alphaMap = canvasTex(oc); sh.alphaMap.__own = 1; sh.depthWrite = false;
          g.add(new T.Mesh(panel(bx + 2.4, by + 3.6, bw, bh, fz + 0.006), sh));
        }

        var om = metalMat(mt.obj, satin ? 0.30 : 0.115, satin);
        om.alphaMap = canvasTex(oc); om.alphaMap.__own = 1;
        om.bumpMap = canvasTex(ob); om.bumpMap.__own = 1; om.bumpScale = 0.020;
        om.transparent = true; om.depthWrite = false; om.alphaTest = 0.12;
        g.add(new T.Mesh(panel(bx, by, bw, bh, fz + 0.018), om));
      }

      /* artwork inside the medallion */
      var a = st.art && st.art[key];
      if (a && a.type && a.canvas && art) {
        var size = art.r * 2 * 0.94 * (a.s || 1);
        var pg = new T.PlaneGeometry(size / U, size / U, 16, 16);
        pg.translate(v3x(art.cx + (a.x || 0)), v3y(art.cy + (a.y || 0)), fz + 0.024);
        if (a.r2) pg.rotateZ(-(a.r2) * Math.PI / 180);
        bend(pg);
        var am;
        if (a.type === 'clip') {
          am = metalMat(mt.obj, satin ? 0.30 : 0.13, satin);
          am.alphaMap = canvasTex(a.canvas); am.alphaMap.__own = 1; am.alphaTest = 0.14;
        } else {
          am = new T.MeshStandardMaterial({ color: 0xffffff, roughness: 0.36, metalness: 0.05, envMapIntensity: 0.7 });
          am.map = canvasTex(a.canvas, true); am.map.__own = 1;
        }
        am.transparent = true; am.depthWrite = false;
        g.add(new T.Mesh(pg, am));
      }

      /* banners + lettering — always the very top of the stack */
      live.forEach(function (L) {
        var sl = L.slot, cfg = L.cfg;
        var onField = !!(pl.slotField && pl.slotField[sl.id]);
        /* Draw the band on the tier it was fitted to. Using the top tier for
           everything floated the outer plaques a whole tier above the ring they
           are supposed to be screwed to — the "nameplate in the air" look. */
        var tIdx = pl.slotTier ? pl.slotTier[sl.id] : undefined;
        var z = onField ? pl.fieldZ
          : (tIdx !== undefined && pl.tierZ && pl.tierZ[tIdx] !== undefined ? pl.tierZ[tIdx] : pl.topZ);
        var style = cfg.style || st.bannerStyle || 'ribbon';
        var col = pick(data.banners, cfg.color || st.bannerColor) || data.banners[0];
        var surf = z + 0.008;
        if (G.BANNER_HAS_FILL[style]) {
          var shp = G.bannerShape(sl, style);
          if (shp) {
            g.add(new T.Mesh(extrude(shp, 0.024, z + 0.004, true), bannerMat(col.hex)));
            surf = faceZ(z + 0.004, 0.024, true);
            var bb = shp.bbox();
            var edgeP = shp.scale(bb.cx, bb.cy, 1 - 5 / (bb.w / 2), 1 - 3.4 / (bb.h / 2));
            /* A rim, not a plate. Extruding the inner outline on its own laid a
               solid metal face across the whole banner a hair under the enamel,
               and where the strap bends that face broke through as a bright
               streak right over the lettering. Punching it out as a hole leaves
               only the bead around the edge, which is what a real plaque has. */
            g.add(new T.Mesh(extrude(shp, 0.006, surf - 0.002, false, null, edgeP), bandEdgeMat));
          }
        }
        var tc = slotCanvas(sl, style, cfg.text, font.css, { track: 1.4 });
        if (!tc) return;
        var ink = (cfg.ink || st.ink || 'cream');
        var inkHex = ink === 'metal' ? mt.trim : ink === 'black' ? '#151311' : ink === 'white' ? '#f8f6f0' : '#f2e0ad';

        /* A struck-in shadow under every glyph. Engraved lettering on a real belt
           reads because the metal is cut INTO the plate and the cut edge stays
           dark whatever the light does. Without it, polished type on a polished
           plate catches the same highlight as the plate and the word vanishes —
           which is exactly what was happening to WORLD and CHAMPION. */
        var haloC = dilate(tc.canvas, Math.max(3, Math.round(tc.h * 0.075)));
        var hm = new T.MeshBasicMaterial({ color: lin('#0d0b08') });
        hm.alphaMap = canvasTex(haloC); hm.alphaMap.__own = 1;
        hm.transparent = true; hm.depthWrite = false; hm.alphaTest = 0.05; hm.opacity = 0.96;
        var hmesh = new T.Mesh(panel(tc.x, tc.y, tc.w, tc.h, surf + 0.008), hm);
        hmesh.renderOrder = 3;
        g.add(hmesh);

        /* The type itself is satin, never mirror. Engraved metal lettering is
           bead-blasted or brushed in production, so a low roughness plus the
           full studio env is simply wrong as well as unreadable. */
        var tm = ink === 'metal'
          ? new T.MeshPhysicalMaterial({
              color: lin(mt.trim), metalness: 1.0, roughness: 0.42,
              clearcoat: 0.0, envMapIntensity: 0.55
            })
          : new T.MeshPhysicalMaterial({
              color: lin(inkHex), roughness: 0.46, metalness: 0.04,
              clearcoat: 0.16, clearcoatRoughness: 0.34, envMapIntensity: 0.40
            });
        tm.alphaMap = canvasTex(tc.canvas); tm.alphaMap.__own = 1;
        tm.transparent = true; tm.depthWrite = false; tm.alphaTest = 0.10;
        var tmesh = new T.Mesh(panel(tc.x, tc.y, tc.w, tc.h, surf + 0.013), tm);
        tmesh.renderOrder = 4;
        g.add(tmesh);
      });
    });
  };
  function isCenter(k) { return k === 'center'; }

  /* ---------- geometry helpers in belt space ---------- */
  /* These little parts sit ON the bent leather, so they have to be bent with it —
     a flat disc laid on a curved strap disappears under the hide at the edges. */
  function discGeo(x, y, r, z) {
    var g = new T.CircleGeometry(r / U, 48, 0, Math.PI * 2);
    g.translate(v3x(x), v3y(y), z);
    return bend(g);
  }
  function ringGeo(x, y, r0, r1, z) {
    var g = new T.RingGeometry(r0 / U, r1 / U, 48, 2);
    g.translate(v3x(x), v3y(y), z);
    return bend(g);
  }
  function domeGeo(x, y, r, z, squash) {
    var g = new T.SphereGeometry(r / U, 28, 16, 0, Math.PI * 2, 0, Math.PI / 2);
    g.rotateX(Math.PI / 2);
    g.scale(1, 1, squash || 0.6);
    g.translate(v3x(x), v3y(y), z);
    return bend(g);
  }
  /* a shallow dome facing AWAY from the viewer — the cap on the back of a snap.
     Rotated rather than mirrored, so the winding (and therefore the normals and
     the metal's reflections) stay correct. */
  function backDomeGeo(x, y, r, z, squash) {
    var g = new T.SphereGeometry(r / U, 28, 14, 0, Math.PI * 2, 0, Math.PI / 2);
    g.rotateX(-Math.PI / 2);
    g.scale(1, 1, squash || 0.5);
    g.translate(v3x(x), v3y(y), z);
    return bend(g);
  }
  /* grow a canvas's coverage outward by `d` px — used to build the dark cut
     edge that sits under engraved lettering so it never loses its outline */
  function dilate(src, d) {
    var c = document.createElement('canvas');
    c.width = src.width; c.height = src.height;
    var x = c.getContext('2d');
    var k = Math.max(1, d | 0), i;
    var offs = [[-k, 0], [k, 0], [0, -k], [0, k],
                [-k, -k], [k, -k], [-k, k], [k, k],
                [-k * 0.6, 0], [k * 0.6, 0], [0, -k * 0.6], [0, k * 0.6], [0, 0]];
    for (i = 0; i < offs.length; i++) x.drawImage(src, offs[i][0], offs[i][1]);
    return c;
  }

  /* saddle stitch: one InstancedMesh of tiny angled slugs following the path */
  function stitchRun(path, hex, z, alt) {
    var G = root.ZBGeom;
    var pts = G.flatten(path, 6);
    if (!pts.length) return null;
    var step = 13, acc = 0, list = [], i, prev = pts[0];
    for (i = 1; i < pts.length; i++) {
      var d = Math.hypot(pts[i][0] - prev[0], pts[i][1] - prev[1]);
      acc += d;
      if (acc >= step) {
        acc = 0;
        list.push([pts[i][0], pts[i][1], Math.atan2(pts[i][1] - pts[i - 1][1], pts[i][0] - pts[i - 1][0])]);
      }
      prev = pts[i];
    }
    if (!list.length) return null;
    var base = new T.BoxGeometry(8.0 / U, 2.3 / U, 1.9 / U);
    var m = new T.MeshPhysicalMaterial({ color: lin(hex), metalness: 0.02, roughness: 0.72, envMapIntensity: 0.28, clearcoat: 0.15 });
    var inst = new T.InstancedMesh(base, m, list.length);
    var dummy = new T.Object3D();
    list.forEach(function (p, k) {
      var wx = v3x(p[0]), wy = v3y(p[1]);
      dummy.position.set(wx, wy, z + zAt(wx, wy));
      dummy.rotation.set(0, 0, -p[2]);
      dummy.updateMatrix();
      inst.setMatrixAt(k, dummy.matrix);
    });
    inst.instanceMatrix.needsUpdate = true;
    return inst;
  }
  /* The flanking plates are small. A full wreath just turns to mush at that size, so the
     inner pair carries the motif and the outer pair carries a single clean mark. */
  function sideLayout(main, key) {
    var outer = key.indexOf('outer') === 0;
    var m = {
      crest: ['wreathlet', 'starmark'], globe: ['wreathlet', 'starmark'], shield: ['minimal', 'starmark'],
      starburst: ['starburst', 'starmark'], wreath: ['wreathlet', 'starmark'], laurelcrown: ['wreathlet', 'starmark'],
      wings: ['minimal', 'starmark'], rope: ['rope', 'minimal'], chain: ['chain', 'minimal'],
      gems: ['gems', 'starmark'], fluted: ['fluted', 'minimal'], deco: ['minimal', 'minimal'], minimal: ['minimal', 'starmark']
    };
    var e = m[main] || m.minimal;
    return outer ? e[1] : e[0];
  }
  function tplOf(geom) {
    var G = root.ZBGeom;
    for (var i = 0; i < G.TEMPLATES.length; i++) if (G.TEMPLATES[i].id === geom.id) return G.TEMPLATES[i];
    return G.TEMPLATES[0];
  }
  function pick(arr, id) { for (var i = 0; i < (arr || []).length; i++) if (arr[i].id === id) return arr[i]; return null; }

  /* high-resolution still for the cart thumbnail, the PDF and the gallery.
     Renders at 2x and downsamples, which is what keeps the fine engraving crisp. */
  Belt.prototype.capture = function (w, h, view, mime, q) {
    if (!this.ok) return null;
    var el = this.renderer.domElement;
    var old = { w: el.width, h: el.height, fov: this.camera.fov, z: this.camera.position.z, asp: this.camera.aspect, dpr: this.renderer.getPixelRatio() };
    var rx = this.rot.x, ry = this.rot.y, fv = this.front.visible, bv = this.back.visible;
    if (view === 'front') { this.group.rotation.set(0, 0, 0); this.front.visible = true; this.back.visible = false; }
    else if (view === 'angle') { this.group.rotation.set(-0.09, 0.42, 0); this.front.visible = true; this.back.visible = false; }
    else if (view === 'back') { this.group.rotation.set(0, Math.PI, 0); this.front.visible = false; this.back.visible = true; }
    var SS = 2;
    /* a capture is always the whole belt, never the customer's current zoom */
    var oz = this.zoom, op = this.pan;
    this.zoom = 1; this.pan = null;
    this.camera.position.x = 0; this.camera.position.y = 0;
    this.camera.lookAt(0, 0, 0);
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(w * SS, h * SS, false);
    this._frame(w / h);
    this.renderer.render(this.scene, this.camera);
    var url;
    try {
      var out = document.createElement('canvas'); out.width = w; out.height = h;
      var ctx = out.getContext('2d');
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(el, 0, 0, w, h);
      url = out.toDataURL(mime || 'image/png', q === undefined ? 0.92 : q);
    } catch (e) {
      url = el.toDataURL(mime || 'image/png', q === undefined ? 0.92 : q);
    }
    this.group.rotation.set(rx, ry, 0);
    this.front.visible = fv; this.back.visible = bv;
    this.renderer.setPixelRatio(old.dpr);
    this.zoom = oz; this.pan = op;
    this.camera.aspect = old.asp; this.camera.fov = old.fov; this.camera.position.z = old.z;
    this.camera.updateProjectionMatrix();
    this.resize(true);
    this._clampPan();
    return url;
  };

  root.ZB3D = {
    supported: function () {
      try { var c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl'))); } catch (e) { return false; }
    },
    create: function (three, host) { T = three; var b = new Belt(host); return b.ok ? b : null; }
  };
})(typeof window !== 'undefined' ? window : globalThis);
